import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../i18n";
import { MENU, ALLERGEN_NOTE, HERO_IMAGE } from "../data";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import OliveBranch from "../components/OliveBranch";

const MenuItem = ({ item, lang, testid }) => (
  <div
    data-testid={testid}
    className="group flex items-baseline gap-3 py-4 border-b border-qs-border/70 hover:bg-qs-bg2/60 px-3 -mx-3 transition-colors"
  >
    <div className="flex-1 min-w-0">
      <h3 className="font-cormorant text-xl text-qs-text group-hover:text-qs-red transition-colors">
        {item.name}
        {item.al ? <sup className="font-outfit text-[10px] text-qs-text2 ml-1.5 tracking-wide">{item.al}</sup> : null}
      </h3>
      {item.desc?.[lang] ? (
        <p className="font-outfit text-sm text-qs-text2 leading-snug">{item.desc[lang]}</p>
      ) : null}
    </div>
    <div className="flex-1 border-b border-dotted border-qs-border/80 mb-1.5 hidden sm:block min-w-[20px]" />
    <div className="text-right whitespace-nowrap">
      {item.prices ? (
        <div className="space-y-0.5">
          {item.prices.map((p, i) => (
            <div key={i} className="flex items-baseline justify-end gap-2">
              <span className="font-outfit text-[10px] uppercase tracking-widest text-qs-text2">{p.label[lang]}</span>
              <span className="font-cormorant text-lg text-qs-red">€ {p.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <>
          <span className="font-cormorant text-xl text-qs-red">€ {item.price}</span>
          {item.priceLabel ? (
            <span className="block font-outfit text-[10px] uppercase tracking-widest text-qs-text2">{item.priceLabel[lang]}</span>
          ) : null}
        </>
      )}
    </div>
  </div>
);

export default function MenuPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const m = t.menuPage;
  const [menu, setMenu] = useState(MENU);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/menu").then(({ data }) => {
      if (Array.isArray(data) && data.length) setMenu(data);
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-qs-bg">
      {/* Header */}
      <section className="relative pt-36 pb-20 px-5 md:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center flex flex-col items-center">
          <p className="font-outfit text-qs-gold tracking-[0.35em] uppercase text-xs mb-4">{m.eyebrow}</p>
          <h1 className="font-cormorant text-white text-4xl sm:text-5xl lg:text-6xl">{m.title}</h1>
          <OliveBranch className="w-28 text-qs-gold my-5" />
          <p className="font-outfit text-white/80 max-w-xl">{m.subtitle}</p>
        </div>
      </section>

      {/* Menu */}
      <section className="py-20 md:py-28 px-5 md:px-10">
        <div className="max-w-4xl mx-auto space-y-20">
          {menu.map((cat) => (
            <Reveal key={cat.id}>
              <div data-testid={`menu-category-${cat.id}`}>
                <div className="text-center mb-8">
                  <p className="font-script text-qs-gold text-2xl mb-1">{cat.sub[lang]}</p>
                  <h2 className="font-cormorant text-3xl sm:text-4xl text-qs-text">{cat.title[lang]}</h2>
                  <div className="gold-divider mx-auto mt-4" />
                  {cat.note ? (
                    <p className="font-outfit text-xs uppercase tracking-widest text-qs-text2 mt-3">{cat.note[lang]}</p>
                  ) : null}
                </div>

                {cat.items ? (
                  <div className="space-y-1">
                    {cat.items.map((item, i) => (
                      <MenuItem key={i} item={item} lang={lang} testid={`menu-item-${cat.id}-${i}`} />
                    ))}
                  </div>
                ) : null}

                {cat.groups ? (
                  <div className="space-y-10">
                    {cat.groups.map((g, gi) => (
                      <div key={gi}>
                        <div className="flex items-baseline gap-3 mb-2">
                          <h3 className="font-cormorant text-2xl text-qs-red">{g.label[lang]}</h3>
                          {g.sub?.[lang] ? <span className="font-outfit text-xs uppercase tracking-widest text-qs-text2">{g.sub[lang]}</span> : null}
                        </div>
                        <div className="space-y-1">
                          {g.items.map((item, i) => (
                            <MenuItem key={i} item={item} lang={lang} testid={`menu-item-${cat.id}-${gi}-${i}`} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </Reveal>
          ))}

          {/* Allergen legend */}
          <Reveal>
            <div className="bg-qs-bg2 border border-qs-border p-5 text-center">
              <p className="font-outfit text-xs text-qs-text2 leading-relaxed">{ALLERGEN_NOTE[lang]}</p>
            </div>
          </Reveal>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-16">
          <button
            data-testid="menu-back-btn"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 border border-qs-border text-qs-text rounded-full px-8 py-4 text-xs font-semibold tracking-[0.14em] uppercase hover:border-qs-gold transition-colors"
          >
            <ArrowLeft size={16} /> {m.back}
          </button>
          <button
            data-testid="menu-reserve-btn"
            onClick={() => navigate("/#contact")}
            className="inline-flex items-center bg-qs-red text-white rounded-full px-8 py-4 text-xs font-semibold tracking-[0.14em] uppercase hover:bg-qs-reddark transition-colors"
          >
            {m.reserve}
          </button>
        </div>
      </section>
    </div>
  );
}
